import React, { useState, useMemo, useCallback } from 'react'; // Added useCallback
import { ChevronUp, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react'; // Icons for sorting and pagination

// Helper function to safely get nested property values
const getNestedValue = (obj, path) => {
  if (!path) return obj;
  if (!obj) return undefined;
  
  const properties = path.split('.');
  let result = obj;
  
  for (const prop of properties) {
    if (result === null || result === undefined) {
      return undefined;
    }
    if (typeof result !== 'object') {
      return undefined;
    }
    result = result[prop];
  }
  
  return result;
};

const DataTable = ({
  columns,
  data,
  pagination, // { page, limit, total, total_pages, sort_by, sort_order }
  onPageChange,
  onSortChange,
  onRowClick, // Optional: function to call when a row is clicked
  defaultSortColumn = null, // Optional: initial sort column accessor
  defaultSortOrder = 'ASC', // Optional: initial sort order
}) => {
  const [currentSortColumn, setCurrentSortColumn] = useState(pagination?.sort_by || defaultSortColumn);
  const [currentSortOrder, setCurrentSortOrder] = useState(pagination?.sort_order?.toUpperCase() || defaultSortOrder.toUpperCase());

  const handleSort = useCallback((accessor) => { // Wrapped in useCallback
    if (!accessor) return; // Column might not be sortable

    let newSortOrder = 'ASC';
    if (accessor === currentSortColumn) {
      newSortOrder = currentSortOrder === 'ASC' ? 'DESC' : 'ASC';
    }

    setCurrentSortColumn(accessor);
    setCurrentSortOrder(newSortOrder);

    if (onSortChange) {
      onSortChange(accessor, newSortOrder);
    }
  }, [currentSortColumn, currentSortOrder, onSortChange]); // Dependencies for useCallback

  const handlePageClick = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.total_pages && onPageChange) {
      onPageChange(newPage);
    }
  };

  const renderCellContent = (row, column) => {
    const value = getNestedValue(row, column.accessor);
    if (column.render) {
      // If a custom render function is provided, use it
      // Pass the actual nested value to the render function, not the parent object
      const renderedValue = column.render(value);
      // Ensure we never return an object as a React child
      if (renderedValue && typeof renderedValue === 'object' && !React.isValidElement(renderedValue)) {
        console.warn('Render function returned an object instead of a primitive value:', renderedValue);
        return '-';
      }
      return renderedValue;
    }
    // Otherwise, display the value directly, handling null/undefined and objects
    if (value && typeof value === 'object') {
      console.warn('Attempting to render object as React child:', value);
      return '-';
    }
    return value !== null && value !== undefined ? String(value) : '-';
  };

  // Memoize headers to avoid re-rendering unless columns change
  const tableHeaders = useMemo(() => (
    <tr>
      {columns.map((col) => (
        <th
          key={col.accessor || col.header}
          className={`p-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${col.sortable ? 'cursor-pointer hover:bg-gray-100' : ''}`}
          onClick={() => col.sortable && handleSort(col.accessor)}
        >
          <div className="flex items-center">
            {col.header}
            {col.sortable && currentSortColumn === col.accessor && (
              <span className="ml-1">
                {currentSortOrder === 'ASC' ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </span>
            )}
             {col.sortable && currentSortColumn !== col.accessor && (
               // Add a placeholder for alignment when not sorted
               <span className="ml-1 opacity-30"><ChevronDown size={14} /></span>
             )}
          </div>
        </th>
      ))}
    </tr>
  ), [columns, currentSortColumn, currentSortOrder, handleSort]); // Added handleSort to dependencies

  // Memoize rows to avoid re-rendering unless data changes
  const tableRows = useMemo(() => (
    data.map((row, rowIndex) => (
      <tr
        key={row.TMSId || rowIndex} // Use TMSId if available, otherwise index
        className={`border-b border-gray-200 ${onRowClick ? 'cursor-pointer hover:bg-gray-50' : ''}`}
        onClick={() => onRowClick && onRowClick(row)}
      >
        {columns.map((col) => (
          <td key={col.accessor || col.header} className="p-3 text-sm text-gray-700 text-left"> {/* Ensure text-left, removed whitespace-nowrap */}
            {renderCellContent(row, col)}
          </td>
        ))}
      </tr>
    ))
  ), [data, columns, onRowClick]); // Re-render rows if data, columns, or onRowClick handler changes

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200 bg-white">
        <thead className="bg-gray-50">
          {tableHeaders}
        </thead>
        <tbody className="divide-y divide-gray-200">
          {tableRows}
        </tbody>
      </table>

      {/* Pagination Controls */}
      {pagination && pagination.total_pages > 1 && (
        <div className="flex items-center justify-between p-4 border-t border-gray-200 bg-white">
          <div className="text-sm text-gray-700">
            Page <span className="font-medium">{pagination.page}</span> of <span className="font-medium">{pagination.total_pages}</span>
            <span className="ml-4">({pagination.total} total records)</span>
          </div>
          <div className="flex items-center space-x-1">
            <button
              onClick={() => handlePageClick(1)}
              disabled={pagination.page === 1}
              className="p-1 rounded border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="First page"
            >
              &laquo; First
            </button>
            <button
              onClick={() => handlePageClick(pagination.page - 1)}
              disabled={pagination.page === 1}
              className="p-1 rounded border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Previous page"
            >
              <ChevronLeft size={18} />
            </button>
            {/* Consider adding page number links/input for many pages */}
            <span className="px-2 text-sm">Page {pagination.page}</span>
            <button
              onClick={() => handlePageClick(pagination.page + 1)}
              disabled={pagination.page === pagination.total_pages}
              className="p-1 rounded border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Next page"
            >
              <ChevronRight size={18} />
            </button>
             <button
              onClick={() => handlePageClick(pagination.total_pages)}
              disabled={pagination.page === pagination.total_pages}
              className="p-1 rounded border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Last page"
            >
              Last &raquo;
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataTable;
